import {ThemeEvents} from "@theme/events";

/**
 * Nova Price PDP - Custom price update handler
 * Updates price from variant HTML or JSON when variant changes.
 *
 * Listens on document (same pattern as nova-centralio-modal / nova-product-description)
 * so quick-add modal + variant-picker__form updates always reach us.
 */
class NovaPricePDP extends HTMLElement {
	/** @type {((event: Event) => void) | null} */
	#onVariantUpdate = null;

	connectedCallback() {
		this.#onVariantUpdate = this.updatePrice.bind(this);
		document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
	}

	disconnectedCallback() {
		if (this.#onVariantUpdate) {
			document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
			this.#onVariantUpdate = null;
		}
	}

	updatePrice = event => {
		if (!(event.target instanceof Element)) return;
		if (!this.querySelector('[ref="priceContainer"]')) return;

		// variant-picker dispatches variant:update; form controls use .variant-picker__form inside it
		const picker = event.target.closest("variant-picker");
		if (!picker) return;

		// Same product as the picker that changed (skip other products / recommendation pickers)
		if (event.detail?.data?.newProduct) {
			this.dataset.productId = event.detail.data.newProduct.id;
		} else if (
			this.dataset.productId &&
			picker.dataset.productId &&
			String(picker.dataset.productId) !== String(this.dataset.productId)
		) {
			return;
		}

		// Quick-add modal vs PDP: only pair price with picker from the same surface
		const priceModalRoot = this.closest("#quick-add-modal-content");
		const pickerModalRoot = picker.closest("#quick-add-modal-content");
		if (Boolean(priceModalRoot) !== Boolean(pickerModalRoot)) return;
		if (priceModalRoot && pickerModalRoot && priceModalRoot !== pickerModalRoot) return;

		// First, try to get updated price from HTML response (prefer main product grid)
		if (event.detail?.data?.html) {
			const newPriceBlock =
				event.detail.data.html.querySelector("[data-product-grid-content] nova-price-pdp [ref='priceContainer']") ||
				event.detail.data.html.querySelector('nova-price-pdp [ref="priceContainer"]');
			if (newPriceBlock) {
				const currentPriceContainer = this.querySelector('[ref="priceContainer"]');
				if (currentPriceContainer && newPriceBlock.innerHTML !== currentPriceContainer.innerHTML) {
					currentPriceContainer.innerHTML = newPriceBlock.innerHTML;
					return;
				}
			}
		}

		// Fallback: update from variant data (script inside this picker, like nova-centralio-modal)
		const variant = event.detail?.resource;
		if (!variant) return;

		let variantData = variant;
		if (!variantData.price) {
			const script = picker.querySelector('script[type="application/json"]');
			if (script) {
				try {
					variantData = JSON.parse(script.textContent);
				} catch (e) {
					console.warn("Failed to parse variant data", e);
					return;
				}
			}
		}

		if (!variantData.price) return;

		const priceContainer = this.querySelector('[ref="priceContainer"]');
		if (!priceContainer) return;

		const priceNumeric = variantData.price;
		const compareAtPriceNumeric = variantData.compare_at_price || 0;
		const showComparePrice = compareAtPriceNumeric > priceNumeric && compareAtPriceNumeric > 0;

		const priceFormatted = this.formatMoney(priceNumeric);
		const compareAtPriceFormatted = compareAtPriceNumeric > 0 ? this.formatMoney(compareAtPriceNumeric) : "";

		if (showComparePrice) {
			const saleEl = priceContainer.querySelector(".nova-price-sale");
			const originalEl = priceContainer.querySelector(".nova-price-original");
			const discountTag = priceContainer.querySelector(".nova-discount-tag");

			if (saleEl) saleEl.textContent = priceFormatted;
			if (originalEl) originalEl.textContent = compareAtPriceFormatted;

			if (discountTag && compareAtPriceNumeric > priceNumeric) {
				const discountPercentage = Math.round(((compareAtPriceNumeric - priceNumeric) / compareAtPriceNumeric) * 100);
				const discountCents = compareAtPriceNumeric - priceNumeric;
				const discountAmount = discountCents / 100;

				const locale = window.Shopify?.locale || "fr";
				const isFrench = locale.toLowerCase().startsWith("fr");

				if (discountPercentage >= 10) {
					discountTag.textContent = `-${discountPercentage}%`;
					discountTag.style.display = "";
				} else {
					const isRound = discountCents % 100 === 0;
					let discountFormatted;

					if (isFrench) {
						if (isRound) {
							discountFormatted = Math.round(discountAmount).toLocaleString("fr-CA");
						} else {
							discountFormatted = discountAmount.toLocaleString("fr-CA", {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							});
						}
						discountTag.textContent = `-${discountFormatted} $`;
					} else {
						if (isRound) {
							discountFormatted = Math.round(discountAmount).toLocaleString("en-US");
						} else {
							discountFormatted = discountAmount.toLocaleString("en-US", {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							});
						}
						discountTag.textContent = `-$${discountFormatted}`;
					}
					discountTag.style.display = "";
				}
			}

			const priceGroup = priceContainer.querySelector(".nova-price-group");
			const regularPrice = priceContainer.querySelector(".nova-price-regular");
			if (priceGroup) priceGroup.style.display = "flex";
			if (regularPrice) regularPrice.style.display = "none";
		} else {
			const regularPrice = priceContainer.querySelector(".nova-price-regular");
			const priceGroup = priceContainer.querySelector(".nova-price-group");
			const discountTag = priceContainer.querySelector(".nova-discount-tag");

			if (regularPrice) {
				regularPrice.textContent = priceFormatted;
				regularPrice.style.display = "";
			}
			if (priceGroup) priceGroup.style.display = "none";
			if (discountTag) discountTag.style.display = "none";
		}
	};

	/**
	 * Formats variant price in storefront money. Uses Shopify.formatMoney when available.
	 * Never infers “no decimals” from French strings like "399,99 $" (comma decimal) — that used to
	 * round 399,99 $ to 400 $ when the regex only looked for a dot.
	 */
	formatMoney(cents) {
		if (typeof cents !== "number" || Number.isNaN(cents)) return "";

		if (window.Shopify?.formatMoney) {
			return window.Shopify.formatMoney(cents);
		}

		const dollars = cents / 100;
		const hasCents = cents % 100 !== 0;
		const locale = (window.Shopify?.locale || "").toLowerCase();
		const isFrench = locale.startsWith("fr");

		const digitOptions = hasCents
			? {minimumFractionDigits: 2, maximumFractionDigits: 2}
			: {minimumFractionDigits: 0, maximumFractionDigits: 0};

		if (isFrench) {
			const num = dollars.toLocaleString("fr-CA", digitOptions);
			return `${num.replace(/ /g, "\u00a0")} $`;
		}

		return `$${dollars.toLocaleString("en-CA", digitOptions)}`;
	}
}

if (!customElements.get("nova-price-pdp")) {
	customElements.define("nova-price-pdp", NovaPricePDP);
}
